import type { DebugAuditFile } from "../audit";
import type {
  TelemetryIntegrationOptions,
  TelemetryIntegrationProvider,
  TelemetryIntegrationWarning,
} from "./types";
import { createDynatraceTelemetryIntegrationProvider } from "./telemetryIntegrationDynatrace";

const MAX_TRACKED_SESSIONS = 256;

export type TelemetryIntegrationDispatchReason = "stop" | "error" | "disconnected";

export interface TelemetryIntegrationDispatchContext {
  reason: TelemetryIntegrationDispatchReason;
  rawAudit: DebugAuditFile;
}

export interface TelemetryIntegrationAuditPayload {
  schemaVersion: 1;
  kind: DebugAuditFile["kind"];
  generatedAt: string;
  sessionId: string;
  label: string | null;
  requestedProvider: DebugAuditFile["requestedProvider"];
  resolvedProvider: DebugAuditFile["resolvedProvider"];
  sessionDurationMs: number | null;
  metadata: Record<string, unknown> | null;
  report: {
    summary: DebugAuditFile["report"]["summary"];
    health: DebugAuditFile["report"]["health"];
    findings: DebugAuditFile["report"]["findings"];
    dashboard: DebugAuditFile["report"]["dashboard"];
    timeline: {
      phases: DebugAuditFile["report"]["timeline"]["phases"];
      incidents: DebugAuditFile["report"]["timeline"]["incidents"];
      transcriptMilestones: Array<{
        type: DebugAuditFile["report"]["timeline"]["transcriptMilestones"][number]["type"];
        timestamp: number;
      }>;
    };
  };
}

export interface TelemetryIntegrationProviderAdapter {
  readonly provider: TelemetryIntegrationProvider;
  dispatchAudit(
    payload: TelemetryIntegrationAuditPayload,
    context: TelemetryIntegrationDispatchContext
  ): Promise<void> | void;
}

type WarningCallback = (warning: TelemetryIntegrationWarning) => void;

const cloneWarning = (
  warning: TelemetryIntegrationWarning
): TelemetryIntegrationWarning => ({
  ...warning,
  detail: warning.detail ? { ...warning.detail } : warning.detail,
});

const normalizeIntegrationOptions = (
  options?: TelemetryIntegrationOptions | null
): Required<Omit<TelemetryIntegrationOptions, "provider">> & {
  enabled: boolean;
  provider: TelemetryIntegrationProvider | null;
} => {
  const provider = options?.provider ?? null;
  const enabled = options?.enabled ?? provider !== null;

  return {
    enabled: Boolean(enabled),
    provider,
    dynatrace: {
      ...(options?.dynatrace ?? {}),
    },
  };
};

export const normalizeAuditForIntegration = (
  audit: DebugAuditFile
): TelemetryIntegrationAuditPayload => ({
  schemaVersion: 1,
  kind: audit.kind,
  generatedAt: audit.generatedAt,
  sessionId: audit.sessionId,
  label: audit.label,
  requestedProvider: audit.requestedProvider,
  resolvedProvider: audit.resolvedProvider,
  sessionDurationMs: audit.sessionDurationMs,
  metadata: audit.metadata,
  report: {
    summary: audit.report.summary,
    health: audit.report.health,
    findings: audit.report.findings.map((finding) => ({ ...finding })),
    dashboard: audit.report.dashboard,
    timeline: {
      phases: audit.report.timeline.phases.map((phase) => ({ ...phase })),
      incidents: audit.report.timeline.incidents.map((incident) => ({ ...incident })),
      transcriptMilestones: audit.report.timeline.transcriptMilestones.map((milestone) => ({
        type: milestone.type,
        timestamp: milestone.timestamp,
      })),
    },
  },
});

export class TelemetryIntegrationHub {
  private readonly config: ReturnType<typeof normalizeIntegrationOptions>;
  private readonly onWarning: WarningCallback | null;
  private readonly dispatchedBySession = new Map<string, Set<TelemetryIntegrationDispatchReason>>();
  private readonly providerAdapter: TelemetryIntegrationProviderAdapter | null;

  constructor(options?: TelemetryIntegrationOptions | null, onWarning?: WarningCallback) {
    this.config = normalizeIntegrationOptions(options);
    this.onWarning = onWarning ?? null;
    this.providerAdapter = this.createProviderAdapter();
  }

  public isEnabled(): boolean {
    return this.config.enabled;
  }

  public async dispatchAudit(
    audit: DebugAuditFile,
    reason: TelemetryIntegrationDispatchReason
  ): Promise<boolean> {
    if (!this.config.enabled) {
      return false;
    }

    if (!this.config.provider) {
      this.emitWarning({
        provider: "none",
        code: "provider_not_configured",
        message:
          "Telemetry integration is enabled but no provider was configured. Skipping dispatch.",
        timestamp: Date.now(),
      });
      return false;
    }

    if (this.wasDispatched(audit.sessionId, reason)) {
      return false;
    }
    this.markDispatched(audit.sessionId, reason);

    if (!this.providerAdapter) {
      this.emitWarning({
        provider: this.config.provider,
        code: "provider_not_supported",
        message: `Telemetry integration provider '${this.config.provider}' is not supported.`,
        timestamp: Date.now(),
      });
      return false;
    }

    const payload = normalizeAuditForIntegration(audit);

    try {
      await this.providerAdapter.dispatchAudit(payload, {
        reason,
        rawAudit: audit,
      });
      return true;
    } catch (error) {
      this.emitWarning({
        provider: this.providerAdapter.provider,
        code: "dispatch_failed",
        message: `Telemetry integration dispatch failed for provider '${this.providerAdapter.provider}'.`,
        timestamp: Date.now(),
        detail: {
          errorMessage: error instanceof Error ? error.message : String(error),
        },
      });
      return false;
    }
  }

  private createProviderAdapter(): TelemetryIntegrationProviderAdapter | null {
    if (!this.config.enabled || !this.config.provider) {
      return null;
    }

    switch (this.config.provider) {
      case "dynatrace":
        return createDynatraceTelemetryIntegrationProvider(
          this.config.dynatrace,
          this.emitWarning
        );
      default:
        return null;
    }
  }

  private wasDispatched(sessionId: string, reason: TelemetryIntegrationDispatchReason): boolean {
    return this.dispatchedBySession.get(sessionId)?.has(reason) ?? false;
  }

  private markDispatched(sessionId: string, reason: TelemetryIntegrationDispatchReason) {
    const reasons = this.dispatchedBySession.get(sessionId) ?? new Set();
    reasons.add(reason);
    this.dispatchedBySession.set(sessionId, reasons);

    if (this.dispatchedBySession.size <= MAX_TRACKED_SESSIONS) {
      return;
    }

    const overflow = this.dispatchedBySession.size - MAX_TRACKED_SESSIONS;
    const sessionIds = Array.from(this.dispatchedBySession.keys()).slice(0, overflow);
    sessionIds.forEach((trackedSessionId) => {
      this.dispatchedBySession.delete(trackedSessionId);
    });
  }

  private emitWarning = (warning: TelemetryIntegrationWarning) => {
    if (!this.onWarning) {
      return;
    }

    this.onWarning(cloneWarning(warning));
  };
}

export const createTelemetryIntegrationHub = (
  options?: TelemetryIntegrationOptions | null,
  onWarning?: WarningCallback
): TelemetryIntegrationHub => new TelemetryIntegrationHub(options, onWarning);

